import React, { useMemo } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import RichTextEditor from './WorkflowDesigner/components/RichTextEditor';

const { Text } = Typography;

export const CommerceWpaWelcomeWhatsappPanel = ({
  label,
  welcomeSettingsLoading,
  welcomeTemplateName,
  welcomeTemplateCategory,
  welcomeBodyText,
  welcomeTplForm,
  welcomeTplCreating,
  createWelcomeTemplate,
  welcomeTemplates,
  welcomeTemplatesLoading,
  loadWelcomeTemplates,
  welcomeSettingsSaving,
  saveWelcomeWhatsappSettings,
  setWelcomeTemplateName,
  setWelcomeBodyText,
  setWelcomeTemplateCategory,
  setWelcomeTemplateLanguage,
}) => {
  const preview = (welcomeBodyText || '')
    .replace(/\{\{1\}\}/g, 'demo_user')
    .replace(/\{\{2\}\}/g, '示範用戶');
  const statusColor = (s) => ({ APPROVED: 'green', REJECTED: 'red', PENDING: 'orange' }[s] || 'default');
  const statusLabel = (s) => ({ APPROVED: '已審批', REJECTED: '已拒絕', PENDING: '審核中' }[s] || s);
  const catLabel = (c) => ({ MARKETING: '市場', UTILITY: '工具', AUTHENTICATION: '驗證' }[(c || '').toUpperCase()] || c);

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        {label(
          'wpaPermissions.welcomeWhatsappHint',
          '新 PWA 帳號建立成功後，若帳號有電話且已設定模板，將自動發送 WhatsApp 歡迎訊息。正文可使用 {{1}}=登入帳號、{{2}}=顯示名稱。'
        )}
      </Text>
      <Text type="danger" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
        {label(
          'wpaPermissions.welcomeWhatsappPolicyWarning',
          'WhatsApp 模板僅適用於一般通知、說明或介紹，不得傳送密碼等敏感憑證；初始密碼請改以「Welcome Message → 電郵模板」發送。'
        )}
      </Text>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Card size="small" title={label('wpaPermissions.welcomeActiveTemplate', '目前啟用的歡迎 Template')}>
          <Spin spinning={welcomeSettingsLoading}>
            {welcomeTemplateName ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong style={{ fontFamily: 'monospace' }}>{welcomeTemplateName}</Text>
                <Tag>{catLabel(welcomeTemplateCategory)}</Tag>
                {preview ? (
                  <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, whiteSpace: 'pre-wrap' }}>{preview}</div>
                ) : null}
              </Space>
            ) : (
              <Text type="secondary">{label('wpaPermissions.welcomeNoTemplate', '尚未設定歡迎 Template')}</Text>
            )}
          </Spin>
        </Card>

        <Card title={label('wpaPermissions.welcomeCreateTemplate', '建立新 Welcome Template')}>
          <Form form={welcomeTplForm} layout="vertical" onFinish={createWelcomeTemplate} initialValues={{ category: 'MARKETING', language: 'zh_TW' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="name" label="模板名稱" rules={[{ required: true }, { pattern: /^[a-z0-9_]+$/, message: '小寫字母、數字、底線' }]}>
                  <Input placeholder="wpa_welcome" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="category" label="類別" rules={[{ required: true }]}>
                  <Select>
                    <Select.Option value="MARKETING">{label('wpaPermissions.welcomeCatMarketing', '市場 MARKETING')}</Select.Option>
                    <Select.Option value="UTILITY">{label('wpaPermissions.welcomeCatUtility', '工具 UTILITY')}</Select.Option>
                    <Select.Option value="AUTHENTICATION">{label('wpaPermissions.welcomeCatAuth', '驗證 AUTHENTICATION')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="language" label="語言" rules={[{ required: true }]}>
                  <Select>
                    <Select.Option value="zh_TW">繁體中文</Select.Option>
                    <Select.Option value="zh_CN">简体中文</Select.Option>
                    <Select.Option value="en_US">English</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.category !== cur.category}>
              {({ getFieldValue }) => {
                const cat = (getFieldValue('category') || 'MARKETING').toUpperCase();
                if (cat === 'AUTHENTICATION') {
                  return (
                    <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Text style={{ fontSize: 12 }}>{label('wpaPermissions.welcomeAuthHint', 'AUTHENTICATION 類別由 Meta 產生安全文案，不建議用於一般歡迎訊息。')}</Text>
                    </div>
                  );
                }
                return (
                  <>
                    <Form.Item name="bodyText" label="正文" rules={[{ required: true, message: '請輸入正文' }]}>
                      <Input.TextArea rows={4} placeholder="歡迎使用！您的登入帳號為 {{1}}，顯示名稱 {{2}}。" />
                    </Form.Item>
                    <Form.Item name="footer" label="頁尾（選填）">
                      <Input placeholder="選填" />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={welcomeTplCreating}>提交 Meta 審核</Button>
          </Form>
        </Card>

        <Card
          title={label('wpaPermissions.welcomeTemplateList', '已建立的 Templates')}
          extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadWelcomeTemplates} loading={welcomeTemplatesLoading} />}
        >
          <Spin spinning={welcomeTemplatesLoading}>
            {welcomeTemplates.length === 0 ? (
              <Text type="secondary">{label('common.noData', 'No data')}</Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {welcomeTemplates.map((tpl) => {
                  const body = tpl.components?.find((c) => c.type === 'BODY')?.text || '';
                  const tplCat = (tpl.category || '').toUpperCase();
                  const isActive = tpl.name === welcomeTemplateName;
                  const isApproved = (tpl.status || '').toUpperCase() === 'APPROVED';
                  return (
                    <div
                      key={tpl.id || tpl.name}
                      style={{
                        border: isActive ? '2px solid #52c41a' : '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: 10,
                        background: isActive ? '#f6ffed' : '#fafafa',
                      }}
                    >
                      <Space wrap>
                        <Text strong style={{ fontFamily: 'monospace' }}>{tpl.name}</Text>
                        <Tag color={statusColor(tpl.status?.toUpperCase())}>{statusLabel(tpl.status?.toUpperCase())}</Tag>
                        <Tag>{catLabel(tplCat)}</Tag>
                        {isActive && <Tag color="green">使用中</Tag>}
                      </Space>
                      {body ? <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap' }}>{body}</div> : null}
                      <div style={{ marginTop: 8 }}>
                        {isApproved && !isActive && (
                          <Button
                            size="small"
                            type="primary"
                            loading={welcomeSettingsSaving}
                            onClick={async () => {
                              setWelcomeTemplateName(tpl.name);
                              setWelcomeBodyText(body);
                              setWelcomeTemplateCategory(tplCat || 'MARKETING');
                              setWelcomeTemplateLanguage(tpl.language || 'zh_TW');
                              await saveWelcomeWhatsappSettings(tpl.name, tpl.language || 'zh_TW', tplCat || 'MARKETING', body);
                            }}
                          >
                            設為使用中
                          </Button>
                        )}
                        {isActive && (
                          <Button size="small" type="default" loading={welcomeSettingsSaving} onClick={() => saveWelcomeWhatsappSettings()} style={{ marginLeft: 8 }}>
                            重新儲存
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Space>
            )}
          </Spin>
        </Card>
      </Space>
    </div>
  );
};

const WELCOME_EMAIL_PLACEHOLDER_BUTTONS = [
  { key: 'tpl-login-id', icon: '{{login_id}}', insertText: '{{login_id}}' },
  { key: 'tpl-display-name', icon: '{{display_name}}', insertText: '{{display_name}}' },
  { key: 'tpl-password', icon: '{{password}}', insertText: '{{password}}' },
];

export const CommerceWpaWelcomeEmailPanel = ({
  label,
  welcomeSettingsLoading,
  welcomeEmailSubject,
  setWelcomeEmailSubject,
  welcomeEmailBody,
  setWelcomeEmailBody,
  welcomeEmailSaving,
  saveWelcomeEmailSettings,
}) => {
  const injectPreview = (s) => (s || '')
    .replace(/\{\{login_id\}\}/gi, 'demo_user')
    .replace(/\{\{display_name\}\}/gi, '示範用戶')
    .replace(/\{\{password\}\}/gi, '********')
    .replace(/\{\{pwd\}\}/gi, '********')
    .replace(/\{\{1\}\}/g, 'demo_user')
    .replace(/\{\{2\}\}/g, '示範用戶')
    .replace(/\{\{3\}\}/g, '********');
  const subjPreview = injectPreview(welcomeEmailSubject);
  const bodyPreview = injectPreview(welcomeEmailBody);

  const emailToolbarButtons = useMemo(
    () => WELCOME_EMAIL_PLACEHOLDER_BUTTONS.map((btn) => ({
      ...btn,
      title: btn.key === 'tpl-login-id'
        ? label('wpaPermissions.welcomeInsertLoginId', '插入登入帳號')
        : btn.key === 'tpl-display-name'
          ? label('wpaPermissions.welcomeInsertDisplayName', '插入顯示名稱')
          : label('wpaPermissions.welcomeInsertPassword', '插入初始密碼'),
    })),
    [label]
  );

  const emailToolbarTooltips = useMemo(() => ({
    bold: label('wpaPermissions.welcomeRteBold', '粗體'),
    italic: label('wpaPermissions.welcomeRteItalic', '斜體'),
    underline: label('wpaPermissions.welcomeRteUnderline', '底線'),
    strike: label('wpaPermissions.welcomeRteStrike', '刪除線'),
    header: label('wpaPermissions.welcomeRteHeader', '標題'),
    color: label('wpaPermissions.welcomeRteColor', '文字顏色'),
    background: label('wpaPermissions.welcomeRteBackground', '背景色'),
    listOrdered: label('wpaPermissions.welcomeRteListOrdered', '編號清單'),
    listBullet: label('wpaPermissions.welcomeRteListBullet', '項目符號'),
    align: label('wpaPermissions.welcomeRteAlign', '對齊'),
    link: label('wpaPermissions.welcomeRteLink', '插入連結'),
    clean: label('wpaPermissions.welcomeRteClean', '清除格式'),
  }), [label]);

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
        {label('wpaPermissions.welcomeEmailHint', '新帳號有電郵時發送。{{login_id}}/{{1}}=帳號、{{display_name}}/{{2}}=名稱、{{password}}/{{3}}=建立時密碼（僅當次）。')}
      </Text>
      <Spin spinning={welcomeSettingsLoading}>
        <Card title={label('wpaPermissions.welcomeEmailTemplateTitle', '電郵歡迎模板')}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>{label('wpaPermissions.otpEmailSubject', '郵件主旨')}</Text>
            <Input value={welcomeEmailSubject} onChange={(e) => setWelcomeEmailSubject(e.target.value)} placeholder="歡迎加入 — {{display_name}}" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              {label('wpaPermissions.otpEmailBody', '郵件正文（支援 HTML）')}
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              {label(
                'wpaPermissions.welcomeEmailRteHint',
                '工具列可設定粗體、連結等；右側按鈕可插入帳號／名稱／密碼變量。'
              )}
            </Text>
            <RichTextEditor
              value={welcomeEmailBody}
              onChange={setWelcomeEmailBody}
              height={380}
              placeholder="<p>您好 {{display_name}}，</p><p>Login: {{login_id}}<br/>Password: {{password}}</p>"
              customToolbarButtons={emailToolbarButtons}
              toolbarTooltips={emailToolbarTooltips}
            />
          </div>
          {(welcomeEmailSubject || welcomeEmailBody) && (
            <div style={{ background: '#f0f7ff', border: '1px solid #91caff', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>預覽</Text>
              {welcomeEmailSubject ? <div><Text type="secondary">Subject: </Text>{subjPreview}</div> : null}
              {welcomeEmailBody ? <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: bodyPreview }} /> : null}
            </div>
          )}
          <Button type="primary" loading={welcomeEmailSaving} onClick={saveWelcomeEmailSettings}>{label('common.save', 'Save')}</Button>
        </Card>
      </Spin>
    </div>
  );
};
